import { Component, ViewChild, ElementRef, OnInit, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FlowObjectType, FlowObjectDefinition, FlowObjectTypeCategory } from '../../models/flow-object.model';
import { FlowDefinition, FlowTarget, FlowDefinitionContext } from '../../models/flow.model';
import { BaseComponent } from '../../components/base/base.component';
import { FlowDefinitionService } from '../../services/flow-definition.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../shared/enums';
import { FlowInstanceService } from '../../services/flow-instance.service';
import { AuthService } from '../../services/auth.service';
import { Organizacao, Unidade } from '../../models/organograma.model';
import { OrganogramaService } from '../../services/organograma.service';
import { FlowDefinitionContextService } from '../../services/flow-definition-context.service';
import { Utils } from '../../shared/utils';
import { Title } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { InputDataTaskForm } from '../../models/input-output-data.model';
import { FlowObjectDefinitionService } from '../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';
import { ErrorService } from '../../services/error.service';
import { ConfirmationService } from 'primeng/api';
import { Announcer } from 'src/app/models/announcer.model';
import { AnnouncerService } from 'src/app/services/announcer.service';
import { ConfigSchemaFlowDefinition, LocationType } from 'src/app/models/config-schema.model';

@Component({
    selector: 'flow-definition',
    templateUrl: './flow-definition.component.html',
    styleUrl: './flow-definition.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowDefinitionComponent extends BaseComponent implements OnInit {
    // #region [ViewChild]
    @ViewChild('wrapperRef') wrapperRef: ElementRef;
    @ViewChild('descriptionMessageRef') descriptionMessageRef: ElementRef;
    @ViewChild('descriptionAreaRef') descriptionAreaRef: ElementRef;
    @ViewChild('taskHeaderRef') taskHeaderRef: ElementRef;
    @ViewChild('taskContainerRef') taskContainerRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    FlowTarget = FlowTarget;
    FlowObjectTypeCategory = FlowObjectTypeCategory;
    Enums = Enums;
    Utils = Utils;
    // #endregion

    // #region [props]
    isDirectAccess = false;
    isEmbedded = false;
    isEdocs = false;
    isFromGrouping = false;
    isFromConecta = false;
    shouldLoadServiceFromConecta = false;
    conectaEnvironmentSegment = '';
    // #endregion

    // #region [signals]
    $model = signal(null as FlowDefinition);
    $flowObjectToExecute = signal(null as FlowObjectDefinition);
    $flowDefinitionContext = signal(null as FlowDefinitionContext);
    $publishedFlowDefinitions = signal([] as FlowDefinition[]);
    $papeisUnitIdsArray = signal([] as string[]);
    $papeisOrganizationIdsArray = signal([] as string[]);
    $papeisPatriarchIdsArray = signal([] as string[]);
    $patriarch = signal(new Organizacao({ razaoSocial: Enums.Messages.DefaultEmptyValue, sigla: Enums.Messages.DefaultEmptyValue }));
    $organization = signal(new Organizacao({ razaoSocial: Enums.Messages.DefaultEmptyValue, sigla: Enums.Messages.DefaultEmptyValue }));
    $unit = signal(new Unidade({ nome: Enums.Messages.DefaultEmptyValue, nomeCurto: Enums.Messages.DefaultEmptyValue }));
    $instanceVersion = signal(null as number);
    $isBootstrapFinished = signal(false);
    $isContextInUse = signal(false);
    $shouldShowContextSelector = signal(true);
    $canShowHeaderInfo = signal(true);
    $isShowingDescription = signal(false);
    $unitAcronym = signal(null as string);
    $organizationAcronym = signal(null as string);
    $patriarchAcronym = signal(null as string);
    $announcements = signal([] as Announcer[]);
    $visibleAnnouncements = signal(0);
    $configSchema = signal(null as ConfigSchemaFlowDefinition);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $descriptionHtml = computed(() => this.$model().publishedDescription);
    $hasWhitelistAccess = computed(() => {
        const userUnitIds = this.$papeisUnitIdsArray() || [];
        const userOrganizationIds = this.$papeisOrganizationIdsArray() || [];

        if (!this.$configSchema() || !this.$configSchema().whitelist || this.$configSchema().whitelist.length === 0) {
            return false;
        }

        return this.$configSchema().whitelist.some((item: any) => {
            if (item.type === LocationType.Unit) {
                return userUnitIds.includes(item.id);
            }

            if (item.type === LocationType.Organization) {
                return userOrganizationIds.includes(item.id);
            }

            return false;
        });
    });
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        public authService: AuthService,
        private titleService: Title,
        private flowDefinitionService: FlowDefinitionService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private flowDefinitionContextService: FlowDefinitionContextService,
        private flowInstanceService: FlowInstanceService,
        private cookieService: CookieService,
        private organogramaService: OrganogramaService,
        private errorService: ErrorService,
        private confirmationService: ConfirmationService,
        private announcerService: AnnouncerService,
    ) {
        super(route, router, spinner, toastr);

        this.isEmbedded = window.location.pathname.includes(Enums.PagesPaths.Embedded);
        this.isEdocs = window.location.pathname.includes(Enums.PagesPaths.Edocs);
        this.isDirectAccess = ['1', '2'].includes(this.route.snapshot.queryParamMap.get('d')) || this.isEmbedded || this.isEdocs;
        this.isFromGrouping = this.route.snapshot.queryParamMap.get('d') == '2';
        this.shouldLoadServiceFromConecta = window.location.pathname.includes(Enums.PagesPaths.CatalogService);

        if (atob(this.cookieService.get('prodest-eflow-cnc')).toLowerCase() == 'true') {
            this.isFromConecta = window.location.pathname.includes(Enums.PagesPaths.CatalogService);
        }

        if (this.conectaEnvironmentSegment == '') {
            switch (atob(this.cookieService.get('prodest-eflow-env')).toLowerCase()) {
                case 'des':
                    this.conectaEnvironmentSegment = 'dev.';
                    break;

                case 'hom':
                    this.conectaEnvironmentSegment = 'hom.';
                    break;

                case 'tre':
                    this.conectaEnvironmentSegment = 'hom.';
                    break;
            }
        }
    }

    async ngOnInit() {
        this.$papeisUnitIdsArray.set(Object.keys(this.$currentUser().papeisUnitIds));
        this.$papeisOrganizationIdsArray.set(Object.keys(this.$currentUser().papeisOrganizationIds));
        this.$papeisPatriarchIdsArray.set(Object.keys(this.$currentUser().papeisPatriarchIds));

        try {
            let response;
            if (this.shouldLoadServiceFromConecta) {
                response = await this.flowDefinitionService.getByCatalogServiceId(this.paramId);
            } else {
                response = await this.flowDefinitionService.getById(this.paramId);
            }

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                if (!this.isEmbedded && !this.isEdocs) {
                    this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                }

                return;
            }

            Utils.setSignal(this.$model, response.data);

            this.$unitAcronym.set(this.$model().unitInfo.split(' - ')[0].trim());
            this.$organizationAcronym.set(this.$model().organizationInfo.split(' - ')[0].trim());
            this.$patriarchAcronym.set(this.$model().patriarchInfo.split(' - ')[0].trim());
            this.$configSchema.set(JSON.parse(this.$model().configSchema) as ConfigSchemaFlowDefinition);

            this.titleService.setTitle('{0} | {1} ({2}-{3}-{4})'
                .replaceAll('{0}', this.titleService.getTitle().split('|')[0].trim())
                .replaceAll('{1}', this.$model().publishedName)
                .replaceAll('{2}', this.$patriarchAcronym())
                .replaceAll('{3}', this.$organizationAcronym())
                .replaceAll('{4}', this.$unitAcronym())
            );

            // caso o FlowDefinition pertença a um Context ou não tenha vindo diretamente da tela "Iniciar um Fluxo"
            if (this.$model().contextId != null) {
                const response_ContextInUse = await this.flowDefinitionContextService.isContextInUse(this.$model().contextId);

                if (!response_ContextInUse.isSuccess) {
                    this.toastr.error(response_ContextInUse.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                    if (!this.isEmbedded && !this.isEdocs) {
                        this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                    }

                    return;
                }

                this.$isContextInUse.set(response_ContextInUse.data);

                if (this.$isContextInUse() && !this.isDirectAccess) {
                    const response_Context = await this.flowDefinitionContextService.getById(this.$model().contextId);

                    if (!response_Context.isSuccess) {
                        this.toastr.error(response_Context.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                        if (!this.isEmbedded && !this.isEdocs) {
                            this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                        }

                        return;
                    }

                    Utils.setSignal(this.$flowDefinitionContext, response_Context.data);
                    this.$publishedFlowDefinitions.set(this.$flowDefinitionContext().flowDefinitions
                        .filter(x =>
                            x.version > 1
                            || (x.version == 1 && x.isPublished)
                        ).toSorted((a, b) => a.contextIndex - b.contextIndex)
                    );

                    return;
                }
            }

            this.$shouldShowContextSelector.set(false);

            let flowStarter = this.$model().flowObjectDefinitions.find(x => x.isFlowStarter);
            this.$instanceVersion.set(this.$model().isPublished ? this.$model().version : this.$model().version - 1);

            if (flowStarter == null) {
                this.toastr.error(Enums.Messages.NoFlowStarterError, Enums.Messages.Error, Utils.getToastrErrorOptions());

                if (!this.isEmbedded && !this.isEdocs) {
                    this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                }

                return;
            }

            const response_FormData = await this.flowObjectDefinitionService.getFormSchema(flowStarter.id);

            if (!response_FormData.isSuccess) {
                this.toastr.error(response_FormData.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                if (!this.isEmbedded && !this.isEdocs) {
                    this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
                }

                return;
            }

            flowStarter.formSchema = response_FormData.data;

            Utils.setSignal(this.$flowObjectToExecute, flowStarter);

            if (
                this.$model().targetId == FlowTarget.Unit && !Object.keys(this.$currentUser().papeisUnitIds).includes(this.$model().publishedUnitId)
                || this.$model().targetId == FlowTarget.Organization && !Object.keys(this.$currentUser().papeisOrganizationIds).includes(this.$model().publishedOrganizationId)
                || this.$model().targetId == FlowTarget.Patriarch && !Object.keys(this.$currentUser().papeisPatriarchIds).includes(this.$model().publishedPatriarchId)
            ) {
                this.$canShowHeaderInfo.set(false);

                const response = await this.organogramaService.getUnidade(this.$model().publishedUnitId);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                Utils.setSignal(this.$unit, response.data);
                Utils.setSignal(this.$organization, response.data.organizacao);
                Utils.setSignal(this.$patriarch, response.data.organizacao.organizacaoPai);
            }
        } catch (error) {
            this.toastr.error(Enums.Messages.GeneralError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            try {
                this.errorService.log({
                    url: error.url,
                    user: this.$currentUser() || null,
                    error: error,
                });
            } catch { }

            if (!this.isEmbedded && !this.isEdocs) {
                this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
            }

            this.spinner.hide();
        } finally {
            setTimeout(() => {
                if (!this.$shouldShowContextSelector()) {
                    this.toggleDescription();
                }
            }, 1000);

            setTimeout(() => {
                this.$isBootstrapFinished.set(true);
                setTimeout(() => {
                    if (this.wrapperRef != null) {
                        const element = this.wrapperRef.nativeElement as HTMLElement;
                        element.style.height = '100%';
                        setTimeout(() => {
                            element.style.overflow = 'unset';
                        }, 1000);
                    }

                    if (this.taskContainerRef != null && this.isEmbedded) {
                        this.taskContainerRef.nativeElement.style.paddingTop = `${this.taskHeaderRef.nativeElement.clientHeight}px`;
                    }
                    this.getAnnouncements();
                    this.spinner.hide();
                }, 300);
            }, 300);
        }
    }

    async createFlowInstance(inputData: InputDataTaskForm) {
        Utils.scrollToTop();
        this.toggleDescription(false);

        this.spinner.show('afterSubmit');

        const response = await this.flowInstanceService.create(this.$flowObjectToExecute(), inputData);

        if (!response.isSuccess) {
            // lida especificamente com o erro de PDF bichado que o E-Docs deixa no limbo e nunca retorna capturado
            if (response.message.name == 'TaskHandlerHelperErrorTimeout') {
                window.localStorage.setItem('thhet', '1');
                window.addEventListener('beforeunload', () => { window.localStorage.setItem('thhet', '0'); });

                this.confirmationService.confirm({
                    header: Enums.Messages.Information,
                    message: Enums.Messages.EDocsApiTimeoutMessage,
                    icon: 'pi pi-info-circle',
                    accept: () => {
                        window.localStorage.setItem('thhet', '0');
                        window.location.reload();
                    },
                });
            } else {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
                    .then(() => {
                        if (this.isEmbedded) {
                            this.router.navigate([
                                Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                                this.paramId,
                                Enums.PagesPaths.Embedded.slice(1)
                            ]);
                        } else if (this.isEdocs) {
                            this.router.navigate([
                                Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                                this.paramId,
                                Enums.PagesPaths.Edocs.slice(1)
                            ]);
                        } else {
                            this.router.navigate([
                                Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                                this.paramId
                            ]);
                        }
                    });
            }

            return;
        }

        this.toastr.success(response.message.description, Enums.Messages.Success);

        if (this.isEmbedded) {
            this.router.navigate([
                Enums.PagesPaths.FlowInstance + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                response.data.id,
                Enums.PagesPaths.Embedded.slice(1)
            ]);
        } else if (this.isEdocs) {
            this.router.navigate(Utils.isNotNullOrEmpty(this.paramOrigin)
                ? [
                    Enums.PagesPaths.FlowInstance,
                    response.data.id,
                    Enums.PagesPaths.Edocs.slice(1),
                    Enums.PagesPaths.Origin.slice(1),
                    this.paramOrigin
                ]
                : [
                    Enums.PagesPaths.FlowInstance,
                    response.data.id,
                    Enums.PagesPaths.Edocs.slice(1)
                ]
            );
        } else {
            this.router.navigate([
                Enums.PagesPaths.FlowInstance + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                response.data.id
            ]);
        }

        // limpa rascunhos do formulário corrente
        window.localStorage.removeItem(this.$model().id);
        window.localStorage.removeItem(`${this.$model().id}_default`);
    }

    async loadFlowDefinition(flowDefinitionId: string) {
        this.spinner.show();
        this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
            .then(() => {
                if (this.isEmbedded) {
                    this.router.navigate([
                        Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                        flowDefinitionId,
                        Enums.PagesPaths.Embedded.slice(1)
                    ]);
                } else if (this.isEdocs) {
                    this.router.navigate([
                        Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                        flowDefinitionId,
                        Enums.PagesPaths.Edocs.slice(1)
                    ]);
                } else {
                    this.router.navigate(
                        [Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''), flowDefinitionId],
                        { queryParams: { d: 2 } }
                    );
                }
            });
    }

    async loadContextSelection() {
        if (!this.$shouldShowContextSelector()) {
            this.$shouldShowContextSelector.set(true);

            this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
                .then(() => {
                    if (this.isEmbedded) {
                        this.router.navigate([
                            Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                            this.paramId,
                            Enums.PagesPaths.Embedded.slice(1)
                        ]);
                    } else if (this.isEdocs) {
                        this.router.navigate([
                            Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                            this.paramId,
                            Enums.PagesPaths.Edocs.slice(1)
                        ]);
                    } else {
                        this.router.navigate([
                            Enums.PagesPaths.FlowDefinition + (this.isFromConecta ? Enums.PagesPaths.CatalogService : ''),
                            this.paramId
                        ]);
                    }
                });
        }
    }

    getDescriptionHtml() {
        return this.$model().publishedDescription;
    }

    toggleDescription(setValue?: boolean) {
        this.$isShowingDescription.set(setValue == null ? !this.$isShowingDescription() : setValue);

        setTimeout(() => {
            if (this.descriptionAreaRef == null) return;

            this.descriptionAreaRef.nativeElement.style.height = !this.$isShowingDescription()
                ? '0'
                : `calc(${this.descriptionMessageRef.nativeElement.clientHeight}px + ${this.isEmbedded ? '74px' : '50px'})`;
        }, 100);
    }

    backToEdocs() {
        const resolvedSubdomain = Utils.getResolvedEDocsSubdomain(this.cookieService);
        window.location.href = `https://${resolvedSubdomain}es.gov.br/eflow/novo`;
    }

    // #region [===== private methods =====]
    // #endregion

    private async getAnnouncements() {
        const announcements = await this.announcerService.get();
        this.$announcements.set(announcements);
        this.$visibleAnnouncements.set(announcements.length);
    }
}
